import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Breadcrumb from "../DefaultComponent/BreadCrumb";
import Spinner from "../DefaultComponent/Spinner";
import { BREW_USER_STORAGE_KEY, S3MediaURL } from "../../Config/Config";
import { formatDate } from "../../utils/Helper";
import { DeactivateBrewUserAction } from "../../Redux/Actions/OrganizationAction";
import { EntityTransactionsAction } from "../../Redux/Actions/PaymentAction";
import ManualCreditGrant from "../Payments/ManualCreditGrant/ManualCreditGrant";

const deleteConfirmationPhrase = "DELETE";
const deleteImpacts = [
  "The user account will be marked as inactive and access will be revoked immediately.",
  "The user will no longer be able to log in to the platform using the current account.",
  "Existing user data and historical records will remain available in the system for audit and tracking purposes.",
  "If the user registers again in the future, the same account will be reactivated and previous data may become accessible again.",
];

const displayValue = (value: any) =>
  value === null || value === undefined || value === "" ? "-" : value;

const getUserAvatarSrc = (logo: string | null | undefined) => {
  if (!logo) return "/default-profile-img.png";
  if (logo.startsWith("http://") || logo.startsWith("https://")) return logo;
  return `${S3MediaURL}${logo}`;
};

const UserDetail: React.FC = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const brewUserDeactivateState = useSelector(
    (state: any) => state.DeactivateBrewUserData
  );
  const usersListState = useSelector((state: any) => state.UsersListData);
  const entityTransactionsState = useSelector(
    (state: any) => state.EntityTransactionsData
  );

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openGrantDialog, setOpenGrantDialog] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [deleteTouched, setDeleteTouched] = useState(false);
  const [deleteSubmitted, setDeleteSubmitted] = useState(false);
  const [userData, setUserData] = useState<any>(() => {
    const locationUser = (location.state as any)?.user;
    if (locationUser && String(locationUser?.id) === String(userId)) {
      return locationUser;
    }

    const storedUser = sessionStorage.getItem(BREW_USER_STORAGE_KEY);
    if (!storedUser) return null;

    try {
      const parsedUser = JSON.parse(storedUser);
      return String(parsedUser?.id) === String(userId) ? parsedUser : null;
    } catch (error) {
      return null;
    }
  });

  useEffect(() => {
    const locationUser = (location.state as any)?.user;
    if (locationUser && String(locationUser?.id) === String(userId)) {
      setUserData(locationUser);
      sessionStorage.setItem(BREW_USER_STORAGE_KEY, JSON.stringify(locationUser));
    }
  }, [location.state, userId]);

  useEffect(() => {
    const matchingUser = usersListState?.data?.row?.find(
      (user: any) => String(user?.id) === String(userId)
    );

    if (matchingUser) {
      setUserData(matchingUser);
      sessionStorage.setItem(BREW_USER_STORAGE_KEY, JSON.stringify(matchingUser));
    }
  }, [usersListState?.data?.row, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(EntityTransactionsAction("USER", Number(userId), 5));
    }
  }, [dispatch, userId]);

  const handleCloseDeleteDialog = () => {
    if (deleteSubmitted && brewUserDeactivateState?.loading) return;
    setOpenDeleteDialog(false);
    setDeleteTouched(false);
    setDeleteSubmitted(false);
    setDeletionReason("");
    setDeleteAcknowledged(false);
    setDeleteConfirmationInput("");
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setDeleteTouched(false);
    setDeleteSubmitted(false);
    setDeletionReason("");
    setDeleteAcknowledged(false);
    setDeleteConfirmationInput("");
  };

  useEffect(() => {
    if (!deleteSubmitted) return;

    if (brewUserDeactivateState?.status) {
      sessionStorage.removeItem(BREW_USER_STORAGE_KEY);
      setOpenDeleteDialog(false);
      setDeleteTouched(false);
      setDeleteSubmitted(false);
      setDeletionReason("");
      setDeleteAcknowledged(false);
      setDeleteConfirmationInput("");
      navigate("/brew-users");
      return;
    }

    if (brewUserDeactivateState?.loading === false) {
      setDeleteSubmitted(false);
    }
  }, [
    brewUserDeactivateState?.status,
    brewUserDeactivateState?.loading,
    deleteSubmitted,
    navigate,
  ]);

  const trimmedReason = deletionReason.trim();
  const reasonTooShort = trimmedReason.length > 0 && trimmedReason.length < 5;
  const reasonTooLong = trimmedReason.length > 1500;
  const reasonIsValid =
    trimmedReason.length >= 5 && trimmedReason.length <= 1500;
  const showReasonError = deleteTouched && !reasonIsValid;
  const isDeleteSubmitting = deleteSubmitted && brewUserDeactivateState?.loading;
  const deleteErrorMessage =
    brewUserDeactivateState?.data?.message ||
    brewUserDeactivateState?.error ||
    "";
  const confirmationMatches =
    deleteConfirmationInput.trim() === deleteConfirmationPhrase;
  const showConfirmationError =
    deleteTouched &&
    deleteConfirmationInput.trim().length > 0 &&
    !confirmationMatches;
  const showAcknowledgeError = deleteTouched && !deleteAcknowledged;

  const reasonHelperText = showReasonError
    ? trimmedReason.length === 0
      ? "Reason is required."
      : reasonTooShort
      ? "Reason must be at least 5 characters."
      : reasonTooLong
      ? "Reason must be at most 1500 characters."
      : ""
    : `${trimmedReason.length}/1500`;

  const handleDeleteUser = async () => {
    setDeleteTouched(true);
    if (!userId) return;
    if (!reasonIsValid || !deleteAcknowledged || !confirmationMatches) return;
    setDeleteSubmitted(true);
    await dispatch(
      DeactivateBrewUserAction({
        userId,
        deletion_reason: trimmedReason,
      })
    );
  };

  const profileRows = useMemo(
    () => [
      ["Name", userData?.fullname],
      ["Email", userData?.email],
      ["Username", userData?.username],
      ["Phone", userData?.contact_no],
      ["Country", userData?.country],
      ["Registered", userData?.date_joined ? formatDate(userData?.date_joined) : "-"],
      ["Last Updated", userData?.updated_at ? formatDate(userData?.updated_at) : "-"],
    ],
    [userData]
  );

  const accountRows = useMemo(
    () => [
      ["User ID", userData?.id],
      ["Account Status", userData?.is_active ? "Active" : "Inactive"],
      ["User Type", userData?.is_staff ? "Organization User" : "Brew User"],
      ["Email Verified", typeof userData?.is_email_verified === "boolean"
        ? userData?.is_email_verified
          ? "Yes"
          : "No"
        : "-"],
      ["Role", userData?.role],
    ],
    [userData]
  );

  if (!userData) {
    return (
      <div className="page-wrapper card org-detail-page">
        <div className="col-md-12 d-flex justify-content-between align-items-center">
          <Breadcrumb
            pageTitle="Brew User Details"
            breadcrumbs={[
              { label: "Brew Users", path: "/brew-users", isActive: false },
              { label: "Details", path: "", isActive: true },
            ]}
          />
          <Button variant="outlined" onClick={() => navigate("/brew-users")}>
            Back
          </Button>
        </div>

        <div className="org-detail__content">
          <Card className="org-detail__card user-detail__empty">
            <Typography variant="h6" sx={{ mb: 1 }}>
              User details unavailable
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Open this page from the Brew Users list to load the selected user.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/brew-users")}
              sx={{ alignSelf: "flex-start" }}
            >
              Go to Brew Users
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      {isDeleteSubmitting && <Spinner />}
      <div className="page-wrapper card org-detail-page">
        <div className="col-md-12 d-flex justify-content-between align-items-center">
          <Breadcrumb
            pageTitle="Brew User Details"
            breadcrumbs={[
              { label: "Brew Users", path: "/brew-users", isActive: false },
              { label: "Details", path: "", isActive: true },
            ]}
          />
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        <div className="org-detail__content">
          <div className="org-detail__hero card shadow-sm">
            <div className="org-detail__hero-body">
              <div className="org-detail__logo-wrap">
                <img
                  src={getUserAvatarSrc(userData?.logo)}
                  alt={displayValue(userData?.fullname)}
                  className="org-detail__logo"
                />
              </div>
              <div>
                <p className="org-detail__eyebrow">Brew User</p>
                <h3 className="mb-1">{displayValue(userData?.fullname)}</h3>
                <div className="text-muted">{displayValue(userData?.email)}</div>
                <div className="text-muted">
                  Joined:{" "}
                  {userData?.date_joined
                    ? formatDate(userData?.date_joined)
                    : "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Profile</p>
                  <h5 className="mb-0">User Information</h5>
                </div>
              </div>
              <div className="org-detail__info-grid">
                {profileRows.map(([label, value]) => (
                  <div className="org-detail__info-row" key={label}>
                    <span className="org-detail__info-label">{label}</span>
                    <span className="org-detail__info-value">
                      {displayValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Account</p>
                  <h5 className="mb-0">Status & Access</h5>
                </div>
              </div>
              <Grid container spacing={3} className="mt-1">
                <Grid item xs={12} md={7}>
                  <div className="org-detail__info-grid">
                    {accountRows.map(([label, value]) => (
                      <div className="org-detail__info-row" key={label}>
                        <span className="org-detail__info-label">{label}</span>
                        <span className="org-detail__info-value">
                          {displayValue(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </Grid>
                <Grid item xs={12} md={5}>
                  <div className="org-detail__mini-card">
                    <p className="org-detail__eyebrow">Quick Status</p>
                    <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                      <Chip
                        label={userData?.is_active ? "Active" : "Inactive"}
                        color={userData?.is_active ? "success" : "default"}
                      />
                      <Chip
                        label={userData?.is_staff ? "Organization User" : "Brew User"}
                        color={userData?.is_staff ? "primary" : "secondary"}
                      />
                      <Chip
                        label={`ID: ${displayValue(userData?.id)}`}
                        variant="outlined"
                      />
                    </Stack>
                  </div>
                </Grid>
              </Grid>
            </Card>
          </div>

          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <p className="org-detail__eyebrow">Billing</p>
                <h5 className="mb-0">Recent Transactions</h5>
              </div>

              <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pb: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setOpenGrantDialog(true)}
                >
                  Create Transaction
                </Button>
              </Box>

              {entityTransactionsState?.loading ? (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Spinner />
                </Box>
              ) : entityTransactionsState?.data?.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Transaction ID</strong></TableCell>
                        <TableCell><strong>Plan</strong></TableCell>
                        <TableCell><strong>Credits</strong></TableCell>
                        <TableCell><strong>Bonus</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Source</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entityTransactionsState.data.map((tx: any) => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.transaction_id || "-"}</TableCell>
                          <TableCell>{tx.plan_name || "-"}</TableCell>
                          <TableCell>{displayValue(tx.total_credits)}</TableCell>
                          <TableCell>{displayValue(tx.bonus_credits)}</TableCell>
                          <TableCell>
                            {tx.amount_to_pay != null && tx.currency
                              ? `${tx.currency} ${tx.amount_to_pay}`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {displayValue(
                              tx.payment_source === "MANUAL"
                                ? "Manual"
                                : tx.payment_source
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tx.status}
                              size="small"
                              color={tx.status === "SUCCESS" || tx.status === "COMPLETED" ? "success" : tx.status === "FAILED" ? "error" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {tx.credit_reason
                              ? tx.credit_reason
                              : tx.payment_source === "MANUAL"
                              ? tx.plan_name || "-"
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {tx.created_at ? formatDate(tx.created_at) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transactions found for this user.
                  </Typography>
                </Box>
              )}
            </Card>
          </div>

          <div className="org-detail__grid">
            <Card
              className="org-detail__card org-detail__danger-card"
              sx={{ borderColor: "error.main" }}
            >
              <div className="org-detail__danger-note org-detail__danger-note--centered">
                <div className="org-detail__danger-note-icon">!</div>
                <div>
                  <Typography
                    variant="subtitle2"
                    className="org-detail__danger-note-title"
                  >
                    <h4><strong>Read Before You Continue</strong></h4>
                  </Typography>
                  <Typography
                    variant="body2"
                    className="org-detail__danger-note-text"
                  >
                    Warning: This action will deactivate the user account.
                  </Typography>
                </div>
              </div>

              <div className="org-detail__danger-list">
                {deleteImpacts.map((impact) => (
                  <div key={impact} className="org-detail__danger-item">
                    <span className="org-detail__danger-dot" />
                    <Typography variant="body2">{impact}</Typography>
                  </div>
                ))}
              </div>

              <div className="org-detail__danger-footer">
                <Typography
                  variant="caption"
                  className="org-detail__danger-footer-text"
                >
                  Please proceed only if you fully understand the impact of this
                  action.
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpenDeleteDialog}
                >
                  Deactivate User
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert
              severity="warning"
              variant="filled"
              className="org-detail__dialog-alert"
            >
              This will deactivate <strong>{displayValue(userData?.fullname)}</strong>
              {" "}and revoke access immediately.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please provide a reason for deletion. This is required for audit
              purposes before we submit the request.
            </Typography>
            {deleteErrorMessage && !isDeleteSubmitting && (
              <Alert severity="error">{deleteErrorMessage}</Alert>
            )}
            <TextField
              label="Reason"
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              onBlur={() => setDeleteTouched(true)}
              placeholder="Add a brief explanation for why this user is being deactivated"
              fullWidth
              required
              multiline
              minRows={4}
              error={showReasonError}
              helperText={reasonHelperText}
              inputProps={{ maxLength: 1500 }}
              disabled={isDeleteSubmitting}
              autoFocus
            />
            <div
              className={`org-detail__confirm-panel ${
                deleteAcknowledged ? "org-detail__confirm-panel--active" : ""
              }`}
            >
              <FormControlLabel
                className="org-detail__confirm-check"
                control={
                  <Checkbox
                    checked={deleteAcknowledged}
                    onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                    color="error"
                    disabled={isDeleteSubmitting}
                  />
                }
                label={
                  <span className="org-detail__confirm-check-label">
                    I understand this action will deactivate the account and
                    remove the user&apos;s access immediately.
                  </span>
                }
              />
              {showAcknowledgeError && (
                <Typography
                  variant="caption"
                  color="error"
                  className="org-detail__confirm-error"
                >
                  Please confirm that you understand the impact before deactivating.
                </Typography>
              )}

              {deleteAcknowledged && (
                <div className="org-detail__confirm-type-wrap">
                  <Typography
                    variant="body2"
                    className="org-detail__confirm-type-label"
                  >
                    Final confirmation: type{" "}
                    <strong>{deleteConfirmationPhrase}</strong> to enable the
                    deactivate button. Use capital letters exactly as shown.
                  </Typography>
                  <TextField
                    label={`Type ${deleteConfirmationPhrase} in uppercase`}
                    value={deleteConfirmationInput}
                    onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                    onBlur={() => setDeleteTouched(true)}
                    fullWidth
                    error={showConfirmationError}
                    helperText={
                      showConfirmationError
                        ? `Please type ${deleteConfirmationPhrase} exactly in uppercase to continue.`
                        : `Enter ${deleteConfirmationPhrase} exactly as shown.`
                    }
                    disabled={isDeleteSubmitting}
                  />
                </div>
              )}
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={isDeleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            disabled={
              !reasonIsValid ||
              !deleteAcknowledged ||
              !confirmationMatches ||
              isDeleteSubmitting
            }
          >
            {isDeleteSubmitting ? "Deactivating..." : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      <ManualCreditGrant
        open={openGrantDialog}
        onClose={() => {
          setOpenGrantDialog(false);
          if (userId) {
            dispatch(EntityTransactionsAction("USER", Number(userId), 5));
          }
        }}
        entityType="USER"
        entityId={Number(userId)}
        entityName={userData?.fullname}
        entityEmail={userData?.email}
      />
    </>
  );
};

export default UserDetail;
